import { BRAND } from "../lib/constants";
import { BookNowButton, LabChip } from "./Shared";
import { Phone, Mail, Instagram, Facebook } from "lucide-react";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="relative rounded-[2rem] border border-white/10 overflow-hidden bg-gradient-to-br from-[#060606] to-[#0A0A0A] p-8 sm:p-14 lg:p-20">
          <div className="absolute inset-0 -z-10 opacity-50">
            <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-[#00E5FF]/20 blur-[120px]" />
            <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-white/5 blur-[120px]" />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <LabChip testId="contact-chip">Book · get a quote</LabChip>
              <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
                Ready for a{" "}
                <span className="text-[#00E5FF]">measurably</span>{" "}
                cleaner space?
              </h2>
              <p className="mt-6 text-white/60 font-body text-base sm:text-lg leading-relaxed max-w-xl">
                Book online in under 60 seconds. Choose your service, pick a
                slot, and we'll handle the rest.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <BookNowButton variant="primary" testId="contact-book-now" />
                <a
                  href={BRAND.phoneLink}
                  data-testid="contact-call-btn"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm sm:text-base font-semibold border border-white/20 text-white hover:bg-white/10 transition-all font-body"
                >
                  <Phone size={16} />
                  Call instead
                </a>
              </div>
            </div>

            <div className="space-y-4">
              <a
                href={BRAND.phoneLink}
                data-testid="contact-phone-card"
                className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/40 p-5 hover:border-[#00E5FF]/40 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#00E5FF]/10 flex items-center justify-center text-[#00E5FF]">
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                      Direct line
                    </div>
                    <div className="font-display text-xl font-semibold text-white mt-0.5">
                      {BRAND.phone}
                    </div>
                  </div>
                </div>
                <span className="text-white/30 group-hover:text-[#00E5FF] transition-colors">→</span>
              </a>

              <a
                href={BRAND.emailLink}
                data-testid="contact-email-card"
                className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/40 p-5 hover:border-[#00E5FF]/40 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#00E5FF]/10 flex items-center justify-center text-[#00E5FF]">
                    <Mail size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                      Email
                    </div>
                    <div className="font-display text-base sm:text-lg font-semibold text-white mt-0.5 truncate">
                      {BRAND.email}
                    </div>
                  </div>
                </div>
                <span className="text-white/30 group-hover:text-[#00E5FF] transition-colors">→</span>
              </a>

              <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                  Follow the lab
                </div>
                <div className="mt-3 flex gap-2">
                  {[
                    { icon: Instagram, href: BRAND.socials.instagram, label: "instagram", testId: "social-instagram" },
                    { icon: Facebook, href: BRAND.socials.facebook, label: "facebook", testId: "social-facebook" },
                  ].map(({ icon: Icon, href, label, testId }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={testId}
                      aria-label={label}
                      className="h-11 w-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[#00E5FF] hover:border-[#00E5FF]/40 transition-all"
                    >
                      <Icon size={16} />
                    </a>
                  ))}
                  <a
                    href={BRAND.socials.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="social-tiktok"
                    aria-label="tiktok"
                    className="h-11 w-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[#00E5FF] hover:border-[#00E5FF]/40 transition-all"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V8.75a8.16 8.16 0 004.77 1.52V6.82a4.85 4.85 0 01-1.84-.13z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
